<template>
  <div>
    <form-input-container
      :title="$t('report-of-sickness.first-of-absence')"
      :text="$t('report-of-sickness.form.first-of-absence-description')">

      <date-picker
        v-model="firstOfAbsence"
        :errors="validations.messagesFor('firstOfAbsence')"
        :max="new Date().toISOString()"
        eventColor='#d8d0da'
        :events="date => isDateBeforeMinimalFirstOfAbsence(date)"/>
    </form-input-container>

    <transition name="slide-y-transition">
      <form-input-container v-if="shouldShowARBOInput"
        :title="$t('report-of-sickness.arbo-report')"
        :text="$t('report-of-sickness.form.arbo-report-description')">
        <file-upload placeholder="Upload Bestand" v-model="arboReport" accept='.pdf'>
          <v-icon slot="left">assignment</v-icon>
        </file-upload>
        <span style='float: right; font-size: 0.9em; margin-top: 5px;'>max. 20MB, enkel *.pdf</span>
      </form-input-container>
    </transition>

    <form-input-container
      :title="$t('report-of-sickness.disability-rate')"
      :text="$t('report-of-sickness.form.disability-rate-description')">
      <text-field right
        placeholder="XXX,XX"
        v-model="disabilityRate"
        @change="handleDisabilityRateChanged"
        :errors="validations.messagesFor('disabilityRate')" >
        <p slot="right">%</p>
      </text-field>
    </form-input-container>

    <form-input-container
      :title="$t('report-of-sickness.safetynet')"
      :text="$t('report-of-sickness.form.safetynet-description')">
      <small-info-button slot="text" class="ml-2" @click="this.handleSafetyNetInfoClicked"/>

      <radio-group>
        <radio-group-item :selected="safetyNet === false" :click="() => safetyNet = false" class="mr-4">{{ 'report-of-sickness.form.no-safetynet' | translate}}</radio-group-item>
        <radio-group-item :selected="safetyNet === true" :click="() => safetyNet = true">{{ 'report-of-sickness.form.yes-safetynet' | translate}}</radio-group-item>
      </radio-group>
    </form-input-container>

    <form-input-container
      :title="$t('report-of-sickness.accident')"
      :text="$t('report-of-sickness.form.accident-description')">
      <radio-group>
        <radio-group-item :selected="accident === false" :click="() => accident = false" class="mr-4">{{ 'report-of-sickness.form.no-accident' | translate}}</radio-group-item>
        <radio-group-item :selected="accident === true" :click="() => accident = true">{{ 'report-of-sickness.form.yes-accident' | translate}}</radio-group-item>
      </radio-group>
    </form-input-container>

    <transition name="slide-y-transition">
      <form-input-container :text="$t('report-of-sickness.form.third-party-liability-description')" v-if="accident">
        <radio-group>
          <radio-group-item :selected="thirdPartyLiability === false" :click="() => thirdPartyLiability = false" class="mr-4" >{{ 'report-of-sickness.form.no-third-party-liability' | translate}}</radio-group-item>
          <radio-group-item :selected="thirdPartyLiability === true" :click="() => thirdPartyLiability = true">{{ 'report-of-sickness.form.yes-third-party-liability' | translate}}</radio-group-item>
        </radio-group>
      </form-input-container>
    </transition>
  </div>
</template>

<script>
import Helpers from '@/shared/helpers/functions';
import Compositions from '@/views/helpers/compositions';

import New from '@/views/constants/report-of-sickness-new';
import Edit from '@/views/constants/report-of-sickness-edit';
import Modal from '@/shared/constants/modal';

import DatePicker from '@aevitae/aevitaeui/1_molecules/Input/DatePicker.vue';
import RadioGroup from '@aevitae/aevitaeui/0_atoms/input/RadioGroup.vue';
import RadioGroupItem from '@aevitae/aevitaeui/0_atoms/input/RadioGroupItem.vue';
import TextField from '@aevitae/aevitaeui/0_atoms/input/TextField.vue';
import FileUpload from '@aevitae/aevitaeui/0_atoms/input/FileUpload.vue';
import FormInputContainer from '@aevitae/aevitaeui/0_atoms/containers/FormInputContainer.vue';
import SmallInfoButton from '@aevitae/aevitaeui/1_molecules/buttons/SmallInfoButton.vue';

export default {
  name: 'NewReportOfSicknessForm',
  props: {
    isEditing: Boolean,
    employee: Object
  },
  components: {
    RadioGroup,
    RadioGroupItem,
    TextField,
    FileUpload,
    FormInputContainer,
    SmallInfoButton,
    DatePicker
  },
  computed: {
    reportOfSickness () {
      if (this.isEditing) {
        return this.$store.getters.editReportOfSickness;
      } else {
        return this.$store.getters.newReportOfSickness;
      }
    },

    reportsOfSickness () {
      return this.$store.getters.reportsOfSickness;
    },

    employeeProductTypes () {
      return Compositions.getEmployeePolicyTypes(this.employee, this.$store.getters.activePolicies);
    },

    validations () {
      if (this.isEditing) {
        return this.$store.getters.editReportOfSicknessValidations;
      } else {
        return this.$store.getters.newReportOfSicknessValidations;
      }
    },

    firstOfAbsence: {
      set (value) {
        if (this.minimalFirstOfAbsence != null && new Date(value) <= new Date(this.minimalFirstOfAbsence)) {
          this.$store.dispatch(Modal.Actions.OPEN, {
            component: 'FirstOfAbsenceDuringOrBeforeExistingReportOfSickness'
          });
        } else {
          this.dispatchChange('firstOfAbsence', value);
        }
      },
      get () { return this.reportOfSickness.firstOfAbsence; }
    },

    arboReport: {
      set (value) { this.dispatchChange('arboReport', value); },
      get () { return this.reportOfSickness.arboReport; }
    },

    disabilityRate: {
      set (value) {
        if (value != null) value = value.replace('.', ',');
        this.dispatchChange('disabilityRate', value);
      },
      get () { return this.reportOfSickness.disabilityRate; }
    },

    safetyNet: {
      set (value) { this.dispatchChange('safetyNet', value); },
      get () { return this.reportOfSickness.safetyNet; }
    },

    accident: {
      set (value) { this.dispatchChange('accident', value); },
      get () { return this.reportOfSickness.accident; }
    },

    thirdPartyLiability: {
      set (value) { this.dispatchChange('thirdPartyLiability', value); },
      get () { return this.reportOfSickness.thirdPartyLiability; }
    },

    shouldShowARBOInput () {
      if (this.employeeProductTypes.some(p => p === 'VZM')) {
        if (this.firstOfAbsence) { return Helpers.Dates.differenceFromNowInDays(this.firstOfAbsence) > 7; }
      }
      return false;
    },

    minimalFirstOfAbsence () {
      if (this.reportsOfSickness.hasOwnProperty(this.employee.id)) {
        if (this.reportsOfSickness[this.employee.id].length > 0) {
          let latestDates = this.reportsOfSickness[this.employee.id].map(r => {
            if (this.isEditing && r.id === this.reportOfSickness.id) {
              return null;
            } else {
              return Compositions.getLatestDateFromReportOfSickness(r);
            }
          }).filter(Boolean);
          return latestDates.sort().reverse()[0];
        } else {
          return null;
        }
      } else return null;
    }
  },
  methods: {
    formatDate: (date) => Helpers.Dates.formatDate(date),

    handleDisabilityRateChanged () {
      this.disabilityRate = Helpers.Percentage.formatAfterChange(this.disabilityRate);
    },

    handleSafetyNetInfoClicked () {
      this.$store.dispatch(Modal.Actions.OPEN, {
        component: 'InfoSafetyNetModal'
      });
    },

    dispatchChange (attribute, value) {
      if (this.isEditing) {
        this.$store.dispatch(Edit.Actions.Form.CHANGE, {
          attribute: attribute,
          value: value
        }).then(this.$store.dispatch(Edit.Actions.Steps.Completed.UPDATE));
      } else {
        this.$store.dispatch(New.Actions.Form.CHANGE, {
          attribute: attribute,
          value: value
        }).then(this.$store.dispatch(New.Actions.Steps.Completed.UPDATE));
      }
    },

    isDateBeforeMinimalFirstOfAbsence (date) {
      if (this.minimalFirstOfAbsence == null) return false;
      else return new Date(date) <= new Date(this.minimalFirstOfAbsence);
    }
  }
};
</script>

<style lang="scss" scoped>
  @import '@/assets/stylesheets/variables.scss';

  .input-container {
    padding: 20px 0px;

    p {
      margin-bottom: 30px;
    }
  }
</style>
